import { useState, useEffect } from 'react';
import { Check, ChevronDown, ChevronRight, ChevronUp, Play, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Fancybox from '../../lib/FancyBox';
import { QuizrrStepperSkeleton } from './QuizrrStepperSkeleton';

export function QuizrrStepper({ packId }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [isClosed, setIsClosed] = useState(false);

  const navigate = useNavigate();

  const handleClose = async () => {
    sessionStorage.setItem('Onboarding', 'Closed');
    setIsClosed(true);
    try {
      if (onboardingId) {
        await axios.patch(`/api/pack-onboarding/${onboardingId}/cancel`);
      } else {
        console.warn('Onboarding ID not available, cannot call cancel API.');
      }
    } catch (error) {
      console.error('Failed to cancel onboarding:', error);
    }
  };

  const openVideoModal = (videoUrl, title) => {
    setCurrentVideoUrl(videoUrl);
    setIsVideoModalOpen(true);

    setTimeout(() => {
      const el = document.getElementById('hidden-fancybox-trigger');
      if (el) el.click();
    }, 0);
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;

    const cleanUrl = url.split('https://www.youtube.com/watch?v=').pop();
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = cleanUrl.match(regExp);
    return match && match[2].length === 11 ? match[2] : cleanUrl.substring(0, 11);
  };

  const [activeStep, setActiveStep] = useState(1);
  const [expandedStep, setExpandedStep] = useState(1);
  const [steps, setSteps] = useState([]);
  const [onboardingId, setOnboardingId] = useState(null);
  const [onboardingTestId, setOnboardingTestId] = useState(null);
  const [submittedTest, setSubmittedTest] = useState(null);
  const [attemptedTest, setAttemptedTest] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPackOnboarding = async () => {
    try {
      const { data } = await axios.get(`/api/pack-onboarding/${packId}`);

      return data;
    } catch (apiError) {
      console.error('Error fetching pack onboarding:', apiError);
      throw apiError;
    }
  };

  const updateStepStatus = async (stepsToUpdate, currentOnboardingIdParam) => {
    const idToUse = currentOnboardingIdParam || onboardingId;

    if (!idToUse) {
      console.error('Onboarding ID is not available for updateStepStatus. Cannot update backend.');
      return;
    }
    const formattedSteps = Array.isArray(stepsToUpdate)
      ? stepsToUpdate
      : [{ title: stepsToUpdate.title, status: stepsToUpdate.status }];
    const uniqueStepsToUpdate = Array.from(new Map(formattedSteps.map((step) => [step.title, step])).values());

    if (uniqueStepsToUpdate.length > 0) {
      await axios.patch(`/api/pack-onboarding/${idToUse}`, {
        onboardingSteps: uniqueStepsToUpdate,
      });
    } else {
    }
  };

  const handleStepStatusUpdate = async (stepTitle, status) => {
    try {
      if (status === 'completed') {
        const stepToUpdate = steps.find((step) => step.title === stepTitle);
        if (stepToUpdate && stepToUpdate.isTestAttemptStep && !submittedTest) {
        } else {
          await updateStepStatus({ title: stepTitle, status });
        }
      } else {
        await updateStepStatus({ title: stepTitle, status });
      }

      setSteps((prevSteps) => {
        const updatedSteps = prevSteps.map((step) =>
          step.title === stepTitle ? { ...step, isCompleted: status === 'completed' || status === 'skipped' } : step,
        );

        return updatedSteps;
      });

      if (status === 'completed' || status === 'skipped') {
        const currentStepIndex = steps.findIndex((step) => step.title === stepTitle);
        if (currentStepIndex !== -1 && currentStepIndex < steps.length - 1) {
          const nextStep = currentStepIndex + 2;
          setActiveStep(nextStep);
          setExpandedStep(nextStep);
        }
      }
    } catch (error) {
      console.error('Failed to update step status:', error);
    }
  };

  const transformApiDataToSteps = (apiData) => {
    if (!apiData || !apiData.steps || !Array.isArray(apiData.steps)) {
      console.warn('API data for steps is missing or not an array:', apiData);
      return [];
    }
    const transformed = apiData.steps.map((step, index) => ({
      id: index + 1,
      title: step.title,
      isCompleted: false,
      hasVideo: !!step.video,
      videoSubtitle: step.description,
      videoThumbnail: step.thumbnail,
      video: step.video,
      content: step.description,
      subStepId: `${index + 1}.1`,
      primaryButtonText: step.primaryButton.text,
      secondaryButtonText: step.secondaryButton.text,
      isSkippable: step.isSkippable,
      isTestAttemptStep: step.isTestAttemptStep,
      isTestAnalysisStep: step.isTestAnalysisStep,
    }));

    return transformed;
  };

  const navigateToExploreOtherTest = () => {
    navigate(`/v3/${packId}/tests`);
  };

  const navigateToMistakes = () => {
    if (submittedTest && submittedTest._id) {
      navigate(`/v3/my-mistakes/test/${submittedTest._id}`);
      console.log('route this would navigate to:', `/v3/my-mistakes/test/${submittedTest._id}`);
    } else {
      navigate('/v3/my-mistakes');
    }
  };

  const navigateToTestAnalysis = () => {
    const selectedPackId = localStorage.getItem('selectedPackId');
    if (submittedTest && submittedTest._id && selectedPackId) {
      navigate(`/${selectedPackId}/${submittedTest._id}/analysis`);
      console.log('route this would navigate to:', `/${selectedPackId}/${submittedTest._id}/analysis`);
    }
  };

  useEffect(() => {
    const loadOnboardingData = async () => {
      setLoading(true);
      try {
        const response = await fetchPackOnboarding();

        if (response?.success && response?.data?.packOnboarding) {
          const fetchedOnboardingId = response.data.packOnboarding._id;
          setOnboardingId(fetchedOnboardingId);
          setOnboardingTestId(response.data.packOnboarding.onboardingTest?._id);
          setSubmittedTest(response.data.submittedTest);
          setAttemptedTest(response.data.attemptedTest);
          let transformedSteps = transformApiDataToSteps(response.data.packOnboarding);
          if (response.data.userPackOnboarding && response.data.userPackOnboarding.status) {
            const userStatus = response.data.userPackOnboarding.status;
            transformedSteps = transformedSteps.map((step) => {
              const stepStatus = userStatus.find((status) => status.title === step.title);
              const isCompletedFromAPI =
                stepStatus && (stepStatus.status === 'completed' || stepStatus.status === 'skipped');

              return isCompletedFromAPI ? { ...step, isCompleted: true } : step;
            });
          }

          let stepsToBatchUpdate = [];
          const now = new Date();

          if (response.data.submittedTest) {
            const testAttemptStep = transformedSteps.find((step) => step.isTestAttemptStep);
            const testAnalysisStep = transformedSteps.find((step) => step.isTestAnalysisStep);
            const step1 = transformedSteps.find((s) => s.id === 1);
            const step2 = transformedSteps.find((s) => s.id === 2);

            if (step1 && !step1.isCompleted) {
              step1.isCompleted = true;
              stepsToBatchUpdate.push({ title: step1.title, status: 'completed' });
            }
            if (step2 && !step2.isCompleted) {
              step2.isCompleted = true;
              stepsToBatchUpdate.push({ title: step2.title, status: 'completed' });
            }
            if (testAttemptStep && !testAttemptStep.isCompleted) {
              testAttemptStep.isCompleted = true;
              stepsToBatchUpdate.push({ title: testAttemptStep.title, status: 'completed' });
            }

            if (testAnalysisStep && response.data.submittedTest.detailAccessDate) {
              const detailAccessDate = new Date(response.data.submittedTest.detailAccessDate);
              if (now >= detailAccessDate && !testAnalysisStep.isCompleted) {
                testAnalysisStep.isCompleted = true;
                stepsToBatchUpdate.push({ title: testAnalysisStep.title, status: 'completed' });
              }
            }
          }

          if (stepsToBatchUpdate.length > 0) {
            try {
              await updateStepStatus(stepsToBatchUpdate, fetchedOnboardingId);
              setSteps((currentSteps) => {
                const updated = [...currentSteps];
                stepsToBatchUpdate.forEach((batchStep) => {
                  const index = updated.findIndex((s) => s.title === batchStep.title);
                  if (index !== -1) {
                    updated[index] = { ...updated[index], isCompleted: true };
                  }
                });
                return updated;
              });
            } catch (apiError) {
              console.error('Failed to batch auto-complete steps via API:', apiError);
            }
          } else {
            setSteps(transformedSteps);
          }

          let nextActiveStep = 1;
          let nextExpandedStep = 1;
          const nextIncompleteStep = transformedSteps.find((step) => !step.isCompleted);
          if (nextIncompleteStep) {
            nextActiveStep = nextIncompleteStep.id;
            nextExpandedStep = nextIncompleteStep.id;
          } else {
            nextActiveStep = transformedSteps.length;
            nextExpandedStep = transformedSteps.length;
          }

          setActiveStep(nextActiveStep);
          setExpandedStep(nextExpandedStep);
          setError(false);
        } else if (response?.success === false) {
          setError(true);
          console.error('API responded with success: false', response);
        } else {
          setError(true);
          console.error('Unexpected API response structure or data missing:', response);
        }
      } catch (networkError) {
        setError(true);
        console.error('Failed to load onboarding data due to network/request error:', networkError);
      } finally {
        setLoading(false);
      }
    };

    loadOnboardingData();
  }, [packId]);

  const handleStepClick = (stepId) => {
    const step = steps.find((s) => s.id === stepId);
    if (step && (step.isCompleted || stepId === activeStep)) {
      setExpandedStep(expandedStep === stepId ? 0 : stepId);
    } else {
    }
  };

  if (loading) {
    return <QuizrrStepperSkeleton />;
  }

  if (error || sessionStorage.getItem('Onboarding') === 'Closed') {
    return <></>;
  }

  return (
    <div className="border-t-1 border-b-1 py-3 w-full px-4 md:px-2 md:border-none md:pt-0">
      <div className="mb-6 flex items-start justify-between">
        <div className="flex flex-col text-[var(--q3-neutral-default)] lg:flex-row items-start lg:items-center gap-0 md:gap-3">
          <p className="text-2xl font-bold ">Things you need to do</p>
          <p className="text-sm font-normal">Explore & understand everything about Quizrr Test series</p>
        </div>
        <Button
          variant="outline"
          onClick={handleClose}
          className="h-8 gap-1 flex items-center text-[var(--q3-neutral-default)] border-none rounded-lg shadow-none bg-[var(--q3-surface-dimmer)]"
        >
          <span>Close</span>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="divide-y divide-[var(--q3-stroke-normal)] rounded-lg border-2">
        {steps.map((step) => {
          const isActive = step.id === activeStep;
          const isExpanded = step.id === expandedStep;
          const isCompleted = step.isCompleted;

          return (
            <div
              key={step.id}
              className={cn(
                'overflow-hidden',
                step.id === 1 && 'rounded-t-lg',
                step.id === steps.length && 'rounded-b-lg',
              )}
            >
              <button
                onClick={() => handleStepClick(step.id)}
                className={cn(
                  'flex w-full items-center justify-between p-4 text-left',
                  isExpanded && 'bg-[var(--q3-surface-dim)]',

                  isCompleted ? 'cursor-pointer' : isActive ? 'cursor-pointer' : 'cursor-not-allowed opacity-70',
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full',
                      isCompleted
                        ? 'bg-[var(--q3-success-normal)] text-[var(--q3-neutral-default_inverted)]'
                        : isActive
                          ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-accent-light)]'
                          : 'bg-[var(--q3-surface-dimmer)] ',
                    )}
                  >
                    {isCompleted ? (
                      <div
                        className="border-2 border-[var(--q3-neutral-default_inverted)] rounded-full"
                        style={{ padding: '2px' }}
                      >
                        <Check className="w-4 h-auto" />
                      </div>
                    ) : (
                      <span className={cn(!isActive ? 'text-[var(--q3-neutral-default)]' : '')}>{step.id}</span>
                    )}
                  </div>
                  <span
                    className={cn(
                      'font-medium',
                      isActive
                        ? 'font-bold text-lg text-[var(--q3-accent-normal)]'
                        : 'text-[var(--q3-neutral-default)]',
                    )}
                  >
                    {step.title}
                  </span>
                </div>
                <div className="bg-[var(--q3-accent-light)] rounded-full p-1">
                  {isExpanded ? (
                    <ChevronUp size={18} className="text-[var(--q3-accent-normal)]" />
                  ) : (
                    <ChevronDown size={18} className="text-[var(--q3-accent-normal)]" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="bg-[var(--q3-surface-dim)] px-4 pb-4">
                  {step.hasVideo ? (
                    <div className="flex items-center py-4">
                      <div className="mr-4 text-sm text-[var(--q3-neutral-default)]">{step.subStepId}</div>
                      <div className="flex flex-1 flex-col md:flex-row md:gap-6">
                        <div
                          className="relative mb-4 overflow-hidden rounded-lg md:mb-0 w-[250px] md:w-[250px] md:flex-shrink-0 cursor-pointer"
                          onClick={() => {
                            if (isCompleted) return;
                            if (step.video) {
                              openVideoModal(step.video, step.title);
                            }
                          }}
                        >
                          <img
                            src={step.videoThumbnail}
                            alt={step.title}
                            className="w-full h-auto object-cover rounded-lg"
                          />
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white">
                              <Play className="h-5 w-5" />
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-1 flex-col  ">
                          <h3 className="mb-4 text-xl font-medium text-[var(--q3-neutral-default)]">
                            {step.videoSubtitle}
                          </h3>
                          <div className="mt-auto flex flex-wrap gap-2">
                            <Button
                              className="rounded-full bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]"
                              onClick={() => {
                                handleStepStatusUpdate(step.title, 'completed');
                                if (step.video) {
                                  openVideoModal(step.video, step.title);
                                }
                              }}
                            >
                              <Play className="h-4 w-4" /> {step.primaryButtonText}
                            </Button>
                            <Button
                              variant="outline"
                              className="rounded-full  bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]"
                              disabled={isCompleted}
                              onClick={() => handleStepStatusUpdate(step.title, 'completed')}
                            >
                              {step.secondaryButtonText} <ChevronRight className="h-4 w-4" />
                            </Button>
                            {step.isSkippable && (
                              <Button
                                variant="ghost"
                                className="rounded-full  text-[var(--q3-neutral-light)]"
                                disabled={isCompleted}
                                onClick={() => handleStepStatusUpdate(step.title, 'skipped')}
                              >
                                Skip
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center py-4">
                      <div className="mr-4 text-sm text-[var(--q3-neutral-default)]">{step.subStepId}</div>
                      {step.isTestAttemptStep && !submittedTest && attemptedTest ? (
                        <div className="flex-1">
                          <h3 className="mb-4 text-xl font-medium text-[var(--q3-neutral-default)]">
                            {`Resume your test: ${attemptedTest.title}`}
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            <Button
                              className="rounded-full px-6 bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]"
                              onClick={() => {
                                navigate(`/${packId}/${onboardingTestId}`);
                              }}
                            >
                              Resume <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              className="rounded-full text-[var(--q3-neutral-light)]"
                              disabled={isCompleted}
                              onClick={() => {
                                handleStepStatusUpdate(step.title, 'skipped');
                              }}
                            >
                              Skip
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1">
                          <h3 className="mb-4 text-xl font-medium text-[var(--q3-neutral-default)]">{step.content}</h3>
                          <div className="flex flex-wrap gap-3">
                            <Button
                              className="rounded-full px-6 bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]"
                              disabled={isCompleted}
                              onClick={() => {
                                if (step.isTestAnalysisStep && step.primaryButtonText === 'View My Mistakes') {
                                  navigateToMistakes();
                                  handleStepStatusUpdate(step.title, 'completed');
                                } else if (step.isTestAttemptStep) {
                                  navigateToExploreOtherTest();
                                } else {
                                  handleStepStatusUpdate(step.title, 'completed');
                                }
                              }}
                            >
                              {step.primaryButtonText} <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              className="rounded-full border-slate-200 px-6"
                              disabled={isCompleted}
                              onClick={() => {
                                if (step.isTestAnalysisStep && step.secondaryButtonText === 'View Last Test Analysis') {
                                  navigateToTestAnalysis();
                                  handleStepStatusUpdate(step.title, 'completed');
                                } else if (step.secondaryButtonText === 'Explore Other Test') {
                                  navigateToExploreOtherTest();
                                  handleStepStatusUpdate(step.title, 'completed');
                                } else {
                                  handleStepStatusUpdate(step.title, 'completed');
                                }
                              }}
                            >
                              {step.secondaryButtonText} <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isVideoModalOpen && (
        <Fancybox>
          <a
            href={`https://www.youtube.com/embed/${getYouTubeVideoId(currentVideoUrl)}`}
            data-fancybox
            data-type="iframe"
            style={{ display: 'none' }}
            id="hidden-fancybox-trigger"
          >
            Hidden Fancybox Trigger
          </a>
        </Fancybox>
      )}
    </div>
  );
}
